import React, { useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _isEqual from 'lodash/isEqual';
import { useSelector, useDispatch } from 'react-redux';
import { focusManager } from '@accedo/vdkweb-navigation';
import { Page, FocusDiv } from '@accedo/vdkweb-tv-ui';
import classNames from 'classnames';
import { setVideoContent } from '#/redux/modules/appPlayer/actions';
import {
  getContainerData,
  getContainers,
  getAreContainersReady,
} from '#/redux/modules/brightcove/selectors';
import { loadContainers } from '#/redux/modules/brightcove/actions';

import {
  getBookmarkedVideo,
  getFullscreenBookmarkedVideoContent,
} from '#/redux/modules/appPlayer/selectors';
import { getCurrentProfile } from '#/redux/modules/onboarding/selectors';

import { getCurrentAgeMetadata } from '#/redux/modules/accedoOne/selectors';
import { getVerticalNav, HOME, HEADER } from '#/utils/navigationHelper';

import {
  TopicSwimlaneFactory,
  PlaylistSwimlaneFactory,
  HeroSwimlaneFactory,
  FSCollectionSwimlaneFactory,
  RecentlyWatchedSwimlaneFactory,
} from '#/components/SwimlaneFactory';

import { onVideoTileClick } from '#/components/Swimlane/utils';
import {
  playerConstants,
  containersTypeAlias,
  videoAnalytics,
  SWIMLANE_LENGTH,
  INITIAL_SWIMLANE_COUNT,
} from '#/config/constants';

import styles from './contentLayer.scss';
import assembleCurrentVideo from '#/utils/assembleCurrentVideo';
import {
  isWorkstation,
  isVIZIO,
  isLowEndDevice,
} from '#/utils/componentStyleConfig';

import SimpleScroll from '#/components/Scroll/SimpleScroll';
import LoadingSpinner from '#/components/LoadingSpinner/LoadingSpinner';
import useUpdateScroll from '#/hooks/useUpdateScroll';

import {
  getAgeGradient,
  getOvpContainerIds,
  getContainersWithData,
  getNavIds,
  getOnVideoTileClickContainers,
  getInitialVideoData,
  clearInitialVideoData,
} from './contentLayerUtils';

const { CAROUSEL_CONTAINER, CONTAINER, PAGE } = HOME;

const navMap = {
  PAGE: {
    id: PAGE,
    forwardFocus: CONTAINER,
  },
  CONTAINER: {
    id: CONTAINER,
    forwardFocus: CAROUSEL_CONTAINER,
    nextleft: HEADER.CONTAINER,
    parent: PAGE,
  },
  ...getVerticalNav([CAROUSEL_CONTAINER], {
    parent: CONTAINER,
    useLastFocus: true,
  }),
};

const THRESHOLD = 2;
const HOME_RETURN_FOCUS_ID = 'sensicalHomeReturnFocusId';

let isFirstVideoStarted = false;

export const didFirstVideoStarted = () => {
  return isFirstVideoStarted;
};

function ContentLayer({
  initialContainers = [],
  initialContainersSize = INITIAL_SWIMLANE_COUNT,
}) {
  const dispatch = useDispatch();
  const [focusedElement, updateScroll] = useUpdateScroll(navMap.CONTAINER);
  const [contNav, setContNav] = useState();
  const areContainersReady = useSelector(getAreContainersReady);
  const [containers] = useState(initialContainers);
  const [containersSize, setContainersSize] = useState(initialContainersSize);
  const didRestoreReturnFocusRef = React.useRef(false);
  // const [hasBackgroundVideo, setHasBackgroundVideo] = useState(false);
  const fullscreenBookmarkedVideoContent = useSelector(
    getFullscreenBookmarkedVideoContent,
  );
  const currentProfile = useSelector(getCurrentProfile);

  // Hooks related to getting age category
  const selectedAgeMetadata = useSelector(getCurrentAgeMetadata);

  const ageGradient = useMemo(() => getAgeGradient(selectedAgeMetadata), [
    selectedAgeMetadata?.colorScheme,
  ]);

  // Hooks related to getting container and video data
  const bcContainerIds = useMemo(() => getOvpContainerIds(containers), [
    containers,
  ]);
  const bcContainerItems = useSelector(
    state => getContainerData(state, bcContainerIds),
    _isEqual,
  );

  const bookmarkedVideo = useSelector(state => getBookmarkedVideo(state));
  const bcContainersData = useSelector(getContainers);

  const containersWithData = useMemo(
    () => getContainersWithData(containers, bcContainersData),
    [containers, bcContainersData],
  );

  const navIds = useMemo(() => getNavIds(containers, containersWithData), [
    containers.length,
    containersWithData,
    containersWithData.length,
  ]);

  const onVideoTileClickContainers = useMemo(
    () => getOnVideoTileClickContainers(containers, dispatch),
    [containers],
  );

  useEffect(() => {
    const firstHero = containers.find(
      c => c._meta.typeAlias === containersTypeAlias.CONTAINER_HERO,
    );
    const firstNonHero = containers.find(
      c => c._meta.typeAlias !== containersTypeAlias.CONTAINER_HERO,
    );
    const firstContainer = isWorkstation() ? firstHero : firstNonHero;
    const navKeys = Object.keys(navIds || {});
    const defaultNavId = firstContainer?._meta?.id
      ? `id-${firstContainer._meta.id}`
      : navKeys[0] || CAROUSEL_CONTAINER;
    const returnFocusId = window.sessionStorage.getItem(HOME_RETURN_FOCUS_ID);
    const navId = defaultNavId;
    const focusTargetId = returnFocusId || defaultNavId;
    didRestoreReturnFocusRef.current = Boolean(returnFocusId);

    setContNav({
      ...navMap.CONTAINER,
      forwardFocus: navId,
    });
    if (focusTargetId) {
      requestAnimationFrame(() => {
        focusManager.changeFocus(focusTargetId);
      });
    }
    if (returnFocusId) {
      window.sessionStorage.removeItem(HOME_RETURN_FOCUS_ID);
    }
  }, [containers, navIds]);

  useEffect(() => {
    const limit = isWorkstation() ? 100 : SWIMLANE_LENGTH + 1;

    const onFocusChange = () => {
      containers.forEach((cont, i) => {
        const contId = `id-${cont._meta.id}`;
        if (focusManager.isChildFocused(contId)) {
          setContNav({
            ...navMap.CONTAINER,
            forwardFocus: contId,
          });

          // if we're navigating down then load more swimlanes
          if (i >= containersSize - THRESHOLD) {
            dispatch(
              loadContainers({
                queries: containers
                  .filter(
                    container =>
                      container._meta.typeAlias ===
                      containersTypeAlias.CONTAINER_OVP_PLAYLIST,
                  )
                  .slice(containersSize, containersSize + 3)
                  .map(container => ({
                    playlistId: container.playlistId,
                    containerId: container._meta.id,
                    limit: container.pageLimitPerPlatform + 1 ?? limit,
                  })),
              }),
            );
            setContainersSize(containersSize + 3);
          }
        }
      });
    };
    focusManager.listenToFocusChanged(onFocusChange);

    return () => {
      focusManager.unlistenToFocusChanged(onFocusChange);
    };
  }, [containers, containersSize]);

  // SENSICAL-2931: TV effect to play first item in background
  useEffect(() => {
    const findFirstUnblockedVideoIndex = arr => {
      const res = arr.findIndex(vid =>
        currentProfile?.videosBlocked?.every(
          blockedVideo => blockedVideo.reference_id !== vid.reference_id,
        ),
      );

      return res;
    };

    if (!isWorkstation() && bcContainerItems.hasData && !isLowEndDevice()) {
      bcContainerIds.some(id => {
        if (!bcContainerItems[id]?.length) {
          return false;
        }
        const getFirstUnblockedVideoIndex = findFirstUnblockedVideoIndex(
          bcContainerItems[id],
        );
        const videoToPlay = fullscreenBookmarkedVideoContent || {
          playlists: [id],
          currentPlaylistIndex: 0,
          currentVideoIndex: 0,
          currentVideo: assembleCurrentVideo(
            bcContainerItems[id][
              getFirstUnblockedVideoIndex > 0 ? getFirstUnblockedVideoIndex : 0
            ],
          ),
          initiation: videoAnalytics.initiation.AUTO,
        };
        dispatch(setVideoContent(videoToPlay));

        return true;
      });
    } else if (isVIZIO()) {
      const vizioTimeout = isLowEndDevice()
        ? playerConstants.VIZIO_PLAYER_TIMEOUT
        : 0;

      setTimeout(() => {
        const initialVideoData = getInitialVideoData();

        if (!bookmarkedVideo && bcContainerItems.hasData) {
          bcContainerIds.some(id => {
            if (!bcContainerItems[id]?.length) {
              return false;
            }
            if (initialVideoData) {
              const videoToPlay = {
                playlists: initialVideoData.playlists,
                currentPlaylistIndex: initialVideoData.currentPlaylistIndex,
                currentVideoIndex: initialVideoData.currentVideoIndex,
                currentVideo: initialVideoData.currentVideo,
                initiation: videoAnalytics.initiation.AUTO,
              };
              dispatch(setVideoContent(videoToPlay));
              clearInitialVideoData();
            } else {
              const videoToPlay = {
                playlists: [id],
                currentPlaylistIndex: 0,
                currentVideoIndex: 0,
                currentVideo: assembleCurrentVideo(bcContainerItems[id][0]),
                initiation: videoAnalytics.initiation.AUTO,
              };
              dispatch(setVideoContent(videoToPlay));
            }
            return true;
          });
        }
        isFirstVideoStarted = true;
      }, vizioTimeout);
    }
  }, [bcContainerItems.hasData]);

  // Effect to start playback of first item on app launch when no content has been previously selected
  useEffect(() => {
    // check if no bookmark set and if there is content in the container to play
    if (
      isWorkstation() &&
      !bookmarkedVideo &&
      bcContainerItems.hasData &&
      !containers?.[0]?.disableInWeb &&
      !isVIZIO()
    ) {
      // iterate through containers to play video of first populated swimlane
      bcContainerIds.some(id => {
        if (!bcContainerItems[id]?.length) {
          return false;
        }
        const videoToPlay = bcContainerItems[id][0];
        dispatch(
          setVideoContent({
            playlists: [id],
            currentPlaylistIndex: 0,
            currentVideoIndex: 0,
            currentVideo: assembleCurrentVideo(videoToPlay),
            initiation: videoAnalytics.initiation.AUTO,
          }),
        );
        return true;
      });
    }
  }, [bookmarkedVideo, bcContainerItems.hasData]);

  useEffect(() => {
    if (!isWorkstation() && containers.length) {
      if (didRestoreReturnFocusRef.current) {
        didRestoreReturnFocusRef.current = false;
        return;
      }
      const swimlanes = containers?.filter(
        item => item._meta.typeAlias !== containersTypeAlias.CONTAINER_HERO,
      );
      if (!swimlanes.length) {
        return;
      }
      let navId = `id-${swimlanes[0]._meta.id}`;

      if (fullscreenBookmarkedVideoContent) {
        const {
          currentVideoIndex,
          playlists,
        } = fullscreenBookmarkedVideoContent;
        if (playlists?.[0] !== undefined) {
          navId = `id-${playlists[0]}-VIDEO-${currentVideoIndex}`;
        }
      }
      focusManager.changeFocus(navId);
    }
  }, [containers, fullscreenBookmarkedVideoContent]);
  return (
    <>
      <div
        className={classNames(`${styles.videoOverlay}`, {
          [styles.isVizio]: isVIZIO(),
        })}
        style={{ background: ageGradient }}
      />
      <SimpleScroll focusedElement={focusedElement}>
        <Page
          className={classNames(
            containers?.[0]?.disableInWeb
              ? styles.mainHeroContainer
              : styles.mainContainer,
            { [styles.isVizio]: isVIZIO(), [styles.isTv]: !isWorkstation() },
          )}
          autoFocus
          nav={navMap.PAGE}
        >
          <FocusDiv nav={contNav}>
            {/* TODO:: fix focus forwarding */}
            {containers.length && (
              <FocusDiv
                nav={navMap.CAROUSEL_CONTAINER}
                className={classNames(styles.contentContainer, {
                  [styles.isTv]: !isWorkstation(),
                })}
              >
                {containers?.map((item, index) => {
                  switch (item._meta.typeAlias) {
                    case containersTypeAlias.CONTAINER_RECENTLY_WATCHED:
                      // TODO: When API created to retrieve items
                      return (
                        <RecentlyWatchedSwimlaneFactory
                          containerId={item._meta.id}
                          ageTheme={selectedAgeMetadata}
                          nav={navIds[`id-${item._meta.id}`]}
                          key={`id-${item._meta.id}`}
                          onTileClick={onVideoTileClick(
                            [item._meta.id],
                            dispatch,
                            true,
                            true,
                          )(0)}
                          title={item.displayText}
                          onFocus={updateScroll}
                          fromHome={!isWorkstation()}
                          recentlyWatched
                        />
                      );
                    case containersTypeAlias.CONTAINER_OVP_PLAYLIST:
                      return (
                        <PlaylistSwimlaneFactory
                          containerId={item._meta.id}
                          playlistId={item.playlistId}
                          ageTheme={selectedAgeMetadata}
                          nav={navIds[`id-${item._meta.id}`]}
                          key={`id-${item._meta.id}`}
                          onTileClick={
                            onVideoTileClickContainers[item._meta.id]
                          }
                          title={item.displayText}
                          onFocus={updateScroll}
                          fromHome={!isWorkstation()}
                          alwaysVisible={index === containers.length - 1}
                          livestreamTileEntry={
                            item?.livestreamTilePosition >= 0
                              ? item?.livestreamTileEntry
                              : null
                          }
                          livestreamTilePosition={item?.livestreamTilePosition}
                          sponsoredTilePosition={item?.sponsoredTilePosition}
                          swimlaneSponsor={item?.swimlaneSponsor}
                          sponsoredTileEntry={
                            item?.sponsoredTilePosition >= 0
                              ? item?.sponsoredTileEntry
                              : null
                          }
                        />
                      );
                    case containersTypeAlias.CONTAINER_TOPICS:
                      return (
                        <TopicSwimlaneFactory
                          containerId={item._meta.id}
                          ageTheme={selectedAgeMetadata}
                          nav={navIds[`id-${item._meta.id}`]}
                          key={`id-${item._meta.id}`}
                          title={item.displayText}
                          onFocus={updateScroll}
                          alwaysVisible={index === containers.length - 1}
                        />
                      );
                    case containersTypeAlias.CONTAINER_SHOWS:
                      return (
                        <FSCollectionSwimlaneFactory
                          collectionName={item.fsCollectionName}
                          containerId={item._meta.id}
                          ageTheme={selectedAgeMetadata}
                          nav={navIds[`id-${item._meta.id}`]}
                          key={`id-${item._meta.id}`}
                          title={item.displayText}
                          onFocus={updateScroll}
                          alwaysVisible={index === containers.length - 1}
                          enableSeeAll={false}
                        />
                      );
                    case containersTypeAlias.CONTAINER_HERO:
                      if (item.disableInWeb && isWorkstation()) {
                        return (
                          <HeroSwimlaneFactory
                            ageTheme={selectedAgeMetadata}
                            nav={navIds[`id-${item._meta.id}`]}
                            key={`id-${item._meta.id}`}
                            onFocus={updateScroll}
                            containerId={item._meta.id}
                          />
                        );
                      }
                      return null;
                    default:
                      return null;
                  }
                })}
              </FocusDiv>
            )}
          </FocusDiv>
        </Page>
      </SimpleScroll>
      {!areContainersReady && (
        <LoadingSpinner
          force
          className={classNames(styles.layerSpinner, {
            [styles.isTv]: !isWorkstation(),
          })}
        />
      )}
    </>
  );
}

ContentLayer.propTypes = {
  initialContainers: PropTypes.array,
  initialContainersSize: PropTypes.number,
};

export default ContentLayer;
